import { useCallback, useRef, useState } from "react";

const useProofOfTransactionUpload = ({
  uploadFile,
  onUploadComplete,
  onUploadError,
  onFileClear,
} = {}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const uploadTokenRef = useRef(0);

  const reset = useCallback(() => {
    uploadTokenRef.current += 1;
    setFile(null);
    setPreview("");
    setIsUploading(false);
    setProgress(0);
    setIsReady(false);
    onFileClear?.();
  }, [onFileClear]);

  const startUpload = useCallback(
    async (nextFile) => {
      const token = (uploadTokenRef.current += 1);
      setIsUploading(true);
      setIsReady(false);
      setProgress(0);

      try {
        const url = await uploadFile(nextFile, (percent) => {
          if (uploadTokenRef.current === token) setProgress(percent);
        });

        if (uploadTokenRef.current !== token) return;
        setIsUploading(false);
        setIsReady(true);
        onUploadComplete?.(nextFile, url);
      } catch (error) {
        if (uploadTokenRef.current !== token) return;
        setIsUploading(false);
        setIsReady(false);
        onUploadError?.(error);
      }
    },
    [uploadFile, onUploadComplete, onUploadError],
  );

  const handleChange = useCallback(
    (nextPreview, nextFile) => {
      if (!nextFile) {
        reset();
        return;
      }

      setFile(nextFile);
      setPreview(typeof nextPreview === "string" ? nextPreview : "");
      startUpload(nextFile);
    },
    [reset, startUpload],
  );

  return {
    file,
    preview,
    isUploading,
    progress,
    isReady,
    handleChange,
    reset,
  };
};

export default useProofOfTransactionUpload;
